import { useContext } from "react";
import { useCookies } from "react-cookie";

import Logger from "@/utils/logging";

import { JourneyContext } from "@/features/rapidstor/providers/JourneyProvider";

export interface ExternalData {
    contactData?: Record<string, string | number | boolean>;
    SessionID?: string;
    gclid?: string;
    reservationSource?: string;
    reservationComment?: string;
    persona?: string;
    selectedCustomerType?: string;
}

/**
 * This is a hook because it uses context and cookies. The alternative is passing params but
 * that just means everywhere that needs to generate externalData needs to fetch and parse the params.
 * 
 * Ideally in the future this is just a useReservation hook that handles all reservation related logic.
 * But that's a significant refactor and I'm not sure if it's worth it currently.
 */
export const useExternalData = () => {
    /** Gets the currently selected persona from the journey context */
    const { persona } = useContext(JourneyContext);
    /** Fetch all cookies */
    const [cookies] = useCookies(['contactData', 'sessionId', 'gclid', 'selectedCustomerType']);

    /**
     * Generates the external data object for the reservation request.
     * 
     * @returns {ExternalData} External data object
     */
    const generateExternalData = (): ExternalData => {
        const contactData = cookies.contactData;
        const sessionId = cookies.sessionId;
        const gclid = cookies.gclid;
        const selectedCustomerType = cookies.selectedCustomerType;
        const agentDetails = window.rapidstor_settings?.app_config?.journey?.agentDetails;

        const externalData: ExternalData = {};

        if (contactData) externalData.contactData = contactData;
        if (sessionId) externalData.SessionID = sessionId;
        if (gclid) externalData.gclid = gclid;
        if (agentDetails) {
            /** This data is set by another application, so lets not assume they're always set correctly */
            externalData.reservationSource = agentDetails.agentName ?? undefined;
            externalData.reservationComment = agentDetails.agentComment ?? undefined;
        }
        if (persona) externalData.persona = persona;
        if (selectedCustomerType) externalData.selectedCustomerType = selectedCustomerType;

        Logger.info(`[useExternalData] Generated external data: ${JSON.stringify(externalData)}`);

        return externalData;
    }

    return {
        generateExternalData,
    }

}